// ExplorerResponses.jsx
//
// Admin tab: dashboard + log for the experimental "Utilization Explorer".
// Shows headline stats, a per-case breakdown (call distribution + accuracy vs
// reality, with model/actual markers), and the full submission table.

import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { ArrowPathIcon, BeakerIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { explorerAPI } from '../../services/api';
import { CASES, kdpiColor } from '../experimental/cases';

const OutcomePill = ({ value }) => {
  if (!value) return <span className="text-gray-400">—</span>;
  const utilized = value === 'utilized';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${utilized ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-700'}`}>
      {utilized ? 'Accepted within 100 offers' : 'Not accepted within 100 offers'}
    </span>
  );
};

const MatchPill = ({ matched }) => {
  if (matched === null || matched === undefined) return <span className="text-gray-300">—</span>;
  return matched ? (
    <span className="text-emerald-600 font-semibold text-xs">✓</span>
  ) : (
    <span className="text-rose-600 font-semibold text-xs">✗</span>
  );
};

const StatCard = ({ label, value, sub }) => (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
    <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
    <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
    {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
  </div>
);

const pct = (n, d) => (d > 0 ? Math.round((n / d) * 100) : null);
const pctStr = (n, d) => (d > 0 ? `${pct(n, d)}%` : '—');

// RFC-4180-ish CSV: wrap every field in quotes, double any internal quotes.
const csvCell = (v) => {
  if (v === null || v === undefined) return '""';
  return `"${String(v).replace(/"/g, '""')}"`;
};

const exportResponsesCsv = (responses) => {
  const headers = [
    'id', 'username', 'email', 'case_id', 'case_file', 'user_call', 'reason',
    'model_prediction', 'model_probability', 'true_outcome',
    'matched_model', 'matched_truth', 'created_at',
  ];
  const lines = [headers.map(csvCell).join(',')];
  for (const r of responses) {
    lines.push([
      r.id, r.username, r.email, r.case_id, r.case_file, r.user_call, r.reason,
      r.model_prediction, r.model_probability, r.true_outcome,
      r.matched_model, r.matched_truth, r.created_at,
    ].map(csvCell).join(','));
  }
  // Prepend a BOM so Excel reads UTF-8 correctly.
  const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `explorer-responses-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

// A per-case card: call-distribution bar + accuracy meter + model/actual pills.
const CaseCard = ({ agg }) => {
  const { c, n, saidUtil, saidNot, matchedTruth, modelPred } = agg;
  const accuracy = pct(matchedTruth, n);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="inline-block h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ background: kdpiColor(c.kdpi) }} />
          <div>
            <span className="font-semibold text-gray-900">Case {c.id}</span>
            <span className="text-xs text-gray-500 ml-2">{c.donorAge} y/o · KDPI {c.kdpi}%</span>
          </div>
        </div>
        <span className="text-xs text-gray-400 whitespace-nowrap">{n} response{n !== 1 ? 's' : ''}</span>
      </div>

      <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
        <span>Model: <OutcomePill value={modelPred} /></span>
        <span>Actual: <OutcomePill value={c.trueOutcome} /></span>
      </div>

      {/* Call distribution — stacked bar, every segment directly labeled */}
      <p className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">User calls</p>
      {n === 0 ? (
        <div className="h-2.5 w-full bg-gray-100 rounded" />
      ) : (
        <div className="flex h-2.5 gap-0.5" role="img" aria-label={`${saidUtil} said accepted within 100 offers, ${saidNot} said not accepted within 100 offers`}>
          {saidUtil > 0 && (
            <div className="bg-emerald-500 rounded transition-all" style={{ flexGrow: saidUtil }} title={`Said accepted within 100 offers: ${saidUtil}`} />
          )}
          {saidNot > 0 && (
            <div className="bg-slate-400 rounded transition-all" style={{ flexGrow: saidNot }} title={`Said not accepted within 100 offers: ${saidNot}`} />
          )}
        </div>
      )}
      <div className="flex items-center gap-4 mt-1.5 text-xs text-gray-600">
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-500" />{saidUtil} accepted</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-slate-400" />{saidNot} not</span>
      </div>

      {/* Accuracy vs reality */}
      <div className="mt-3">
        <div className="flex justify-between text-[11px] text-gray-500 mb-1">
          <span>Matched reality</span>
          <span>{n === 0 ? '—' : `${matchedTruth}/${n} (${accuracy}%)`}</span>
        </div>
        <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full bg-primary-500 rounded-full transition-all" style={{ width: `${accuracy || 0}%` }} />
        </div>
      </div>
    </div>
  );
};

const ExplorerResponses = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [responses, setResponses] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await explorerAPI.getResponses({ limit: 1000 });
      setStats(res.data.stats);
      setResponses(res.data.responses || []);
    } catch (e) {
      toast.error('Failed to load explorer responses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Aggregate submissions per case (client-side, from the fetched rows).
  const perCase = useMemo(() => CASES.map((c) => {
    const rows = responses.filter((r) => r.case_id === c.id);
    const n = rows.length;
    const saidUtil = rows.filter((r) => r.user_call === 'utilized').length;
    const matchedTruth = rows.filter((r) => r.matched_truth).length;
    const modelPred = (rows.find((r) => r.model_prediction) || {}).model_prediction || null;
    return { c, n, saidUtil, saidNot: n - saidUtil, matchedTruth, modelPred };
  }), [responses]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center">
            <BeakerIcon className="h-5 w-5 mr-2 text-primary-500" />
            Explorer Responses
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">User predictions & reasoning from the experimental 100-Offer Acceptance Explorer.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => exportResponsesCsv(responses)}
            disabled={loading || responses.length === 0}
            className="flex items-center px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
            Export CSV
          </button>
          <button onClick={load} className="flex items-center px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors">
            <ArrowPathIcon className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Headline stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard label="Total responses" value={stats.total_responses} />
          <StatCard label="Participants" value={stats.participants} />
          <StatCard label="Agreed with model" value={pctStr(stats.matched_model, stats.with_model)} sub={`${stats.matched_model} of ${stats.with_model}`} />
          <StatCard label="Matched reality" value={pctStr(stats.matched_truth, stats.with_truth)} sub={`${stats.matched_truth} of ${stats.with_truth}`} />
        </div>
      )}

      {/* Per-case dashboard */}
      <div className="mb-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h3 className="text-sm font-semibold text-gray-800">Breakdown by case</h3>
          <div className="flex items-center gap-4 text-xs text-gray-600">
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-emerald-500" />Said accepted within 100 offers</span>
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-slate-400" />Said not accepted within 100 offers</span>
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-primary-500" />Matched reality</span>
          </div>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-primary-600" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {perCase.map((agg) => <CaseCard key={agg.c.id} agg={agg} />)}
          </div>
        )}
      </div>

      {/* Full submission log */}
      <h3 className="text-sm font-semibold text-gray-800 mb-3">All responses</h3>
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-primary-600" />
          </div>
        ) : responses.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <BeakerIcon className="h-12 w-12 mx-auto text-gray-300 mb-2" />
            <p className="text-sm">No responses yet.</p>
            <p className="text-xs">Submissions from the 100-Offer Acceptance Explorer will appear here.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                  <th className="px-4 py-3 font-medium">User</th>
                  <th className="px-4 py-3 font-medium">Case</th>
                  <th className="px-4 py-3 font-medium">Their call</th>
                  <th className="px-4 py-3 font-medium min-w-[240px]">Reasoning</th>
                  <th className="px-4 py-3 font-medium">Model</th>
                  <th className="px-4 py-3 font-medium text-center" title="Matched model">vs&nbsp;M</th>
                  <th className="px-4 py-3 font-medium">Actual</th>
                  <th className="px-4 py-3 font-medium text-center" title="Matched reality">vs&nbsp;R</th>
                  <th className="px-4 py-3 font-medium whitespace-nowrap">When</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {responses.map((r) => (
                  <motion.tr key={r.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="hover:bg-gray-50 align-top">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{r.username}</div>
                      <div className="text-xs text-gray-400">{r.email}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="font-medium text-gray-800">Case {r.case_id}</div>
                      <div className="text-xs text-gray-400">{r.case_file}</div>
                    </td>
                    <td className="px-4 py-3"><OutcomePill value={r.user_call} /></td>
                    <td className="px-4 py-3 text-gray-700">
                      {r.reason ? <span className="whitespace-pre-wrap break-words">{r.reason}</span> : <span className="text-gray-300 italic">No reason given</span>}
                    </td>
                    <td className="px-4 py-3">
                      <OutcomePill value={r.model_prediction} />
                      {r.model_probability != null && (
                        <div className="text-[11px] text-gray-400 mt-0.5">p={Number(r.model_probability).toFixed(3)}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center"><MatchPill matched={r.matched_model} /></td>
                    <td className="px-4 py-3"><OutcomePill value={r.true_outcome} /></td>
                    <td className="px-4 py-3 text-center"><MatchPill matched={r.matched_truth} /></td>
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExplorerResponses;
